import { useQuery } from "@tanstack/react-query";
import type { EpisodeRef, ShareLink, ShareScope } from "@dramapie/shared";
import { useEffect, useId, useRef, useState } from "react";

import { createShare, listShares, revokeShare } from "../../lib/api";
import { errorMessage } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import {
  SHARE_DAY_OPTIONS,
  SHARE_SCOPE_OPTIONS,
  absoluteShareUrl,
  copyText,
  isShareExpired,
  maskToken,
  shareExpiryNote,
  shareScopeLabel,
} from "../../lib/m5";
import { LoadingState } from "../states";

export interface ShareDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  /** 当前集：`scope=episode` 时带进请求体。 */
  episodeId?: string | null;
  episodes?: readonly EpisodeRef[];
  onNotify: (kind: "success" | "error" | "info", message: string) => void;
}

/**
 * 分享弹层（顶栏「分享」）。
 *
 * 线上形状（契约 M5 §2 已对齐）：
 * - `POST /shares` 回完整 `{ id, token, url, scope, … }`，完整链接只出现这一次；
 * - `GET /shares` 的 `token` / `url` 都是**掩码** → 列表项不能复制；
 * - 撤销是 `DELETE /api/shares/:id`（用 `share.id`，不拿 token 当主键）。
 * 创建成功后的完整 URL 只放组件内会话状态（不写 localStorage：分享链接等同凭据）。
 * 撤销是**二次确认**：一撤公开页立刻 404，不能误点。
 */
export function ShareDialog({
  projectId,
  open,
  onClose,
  episodeId,
  episodes = [],
  onNotify,
}: ShareDialogProps) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  const [scope, setScope] = useState<ShareScope>("storyboard");
  const [days, setDays] = useState(7);
  const [allowDownload, setAllowDownload] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ShareLink | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "ok" | "fail">("idle");
  /** 二次确认中的分享行（用 id；老数据没有 id 时退回 token 作为行键）。 */
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const sharesQuery = useQuery({
    queryKey: ["shares", projectId],
    queryFn: () => listShares(projectId),
    enabled: open && projectId.length > 0,
    staleTime: 5_000,
    retry: false,
  });
  const shares = sharesQuery.data ?? [];

  useEffect(() => {
    if (!open) {
      setCreated(null);
      setConfirmKey(null);
      setCopyState("idle");
      return;
    }
    const active = document.activeElement;
    restoreRef.current = active instanceof HTMLElement ? active : null;
    const timer = setTimeout(() => closeRef.current?.focus(), 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      restoreRef.current?.focus();
    };
  }, [open, onClose]);

  const currentEpisode = episodeId ? episodes.find((item) => item.id === episodeId) ?? null : null;
  const episodeMissing = scope === "episode" && !episodeId;

  async function handleCreate() {
    if (creating || episodeMissing) return;
    setCreating(true);
    setCopyState("idle");
    try {
      const link = await createShare(projectId, {
        scope,
        ...(scope === "episode" && episodeId ? { episodeId } : {}),
        expiresInDays: days,
        allowDownload,
      });
      // 响应可能缺字段：以表单值为准，保证展示与请求一致。
      setCreated({
        ...link,
        scope,
        episodeId: scope === "episode" ? episodeId ?? null : null,
        allowDownload,
      });
      onNotify("success", "分享链接已生成");
      void sharesQuery.refetch();
    } catch (error) {
      onNotify("error", errorMessage(error, "生成分享链接失败，请稍后重试"));
    } finally {
      setCreating(false);
    }
  }

  async function handleCopy(url: string) {
    const ok = await copyText(url);
    setCopyState(ok ? "ok" : "fail");
    onNotify(ok ? "success" : "error", ok ? "已复制分享链接" : "复制失败，请手动选中链接");
  }

  async function handleRevoke(share: ShareLink) {
    if (revoking) return;
    if (!share.id) {
      onNotify("error", "这条分享缺少 id，无法撤销");
      return;
    }
    setRevoking(share.id);
    try {
      // 撤销用分享记录的 id：token 是对外凭据，不拿它当内部主键。
      await revokeShare(share.id);
      onNotify("success", "分享链接已撤销");
      setConfirmKey(null);
      if (created && created.id === share.id) setCreated(null);
      await sharesQuery.refetch();
    } catch (error) {
      onNotify("error", errorMessage(error, "撤销失败，请稍后重试"));
    } finally {
      setRevoking(null);
    }
  }

  if (!open) return null;

  const createdUrl = created ? absoluteShareUrl(created.url) : "";

  return (
    <div className="dp-scrim" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="dp-dialog dp-share"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="dp-share-head">
          <div>
            <p className="dp-label">share · read only</p>
            <h2 id={titleId} className="dp-share-title">
              分享
            </h2>
            <p className="dp-share-meta">只读外链，不需要登录</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="dp-icon-btn"
            aria-label="关闭分享"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="dp-share-form">
          <div className="dp-share-field">
            <span className="dp-label">范围</span>
            <div className="dp-share-opts" role="radiogroup" aria-label="分享范围">
              {SHARE_SCOPE_OPTIONS.map((option) => {
                const disabled = option.scope === "episode" && !episodeId;
                return (
                  <button
                    key={option.scope}
                    type="button"
                    role="radio"
                    aria-checked={scope === option.scope}
                    aria-label={option.label}
                    disabled={disabled}
                    title={
                      disabled ? "还没选到具体集" : option.hint
                    }
                    className="dp-share-opt"
                    onClick={() => setScope(option.scope)}
                  >
                    <b>{option.label}</b>
                    <span>{option.scope === "episode" && currentEpisode ? `第${currentEpisode.no}集` : option.hint}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="dp-share-field">
            <span className="dp-label">有效期</span>
            <div className="dp-share-opts is-inline" role="radiogroup" aria-label="有效期">
              {SHARE_DAY_OPTIONS.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={days === value}
                  aria-label={`${value} 天`}
                  className="dp-share-opt is-day"
                  onClick={() => setDays(value)}
                >
                  {value} 天
                </button>
              ))}
            </div>
          </div>

          <label className="dp-share-toggle">
            <input
              type="checkbox"
              checked={allowDownload}
              onChange={(event) => setAllowDownload(event.target.checked)}
            />
            <span>
              允许下载
              <em>关闭时只读浏览，视频/图片给独立短时签名</em>
            </span>
          </label>

          <div className="dp-share-actions">
            <button
              type="button"
              className="dp-btn primary"
              onClick={() => void handleCreate()}
              disabled={creating || episodeMissing}
              title={episodeMissing ? "当前集还没解析出来" : "生成只读分享链接"}
            >
              {creating ? "生成中…" : "生成链接"}
            </button>
            <span className="dp-tiny-note">
              {scope === "episode"
                ? `只包含 ${currentEpisode ? `第${currentEpisode.no}集 ${currentEpisode.title}` : "当前集"}`
                : "只包含这一集的整集故事板"}
              · 过期即 404
            </span>
          </div>
        </div>

        {created ? (
          <section className="dp-share-result" aria-label="生成的分享链接">
            <p className="dp-label">链接已生成</p>
            <div className="dp-share-url-row">
              <input
                className="dp-input dp-share-url"
                value={createdUrl}
                readOnly
                aria-label="分享链接"
                onFocus={(event) => event.target.select()}
              />
              <button
                type="button"
                className="dp-btn sm primary"
                onClick={() => void handleCopy(createdUrl)}
              >
                {copyState === "ok" ? "已复制" : "复制"}
              </button>
            </div>
            <p className="dp-share-expiry">
              过期时间 <b>{formatDateTime(created.expiresAt)}</b>
              <span>· {shareExpiryNote(created.expiresAt)}</span>
              <span>· {shareScopeLabel(created.scope, currentEpisode)}</span>
              <span>· {created.allowDownload ? "允许下载" : "仅浏览"}</span>
            </p>
            <p className="dp-share-once">
              完整链接只在这里显示一次；关闭后如需再分享，重新生成一条即可。
            </p>
            {copyState === "fail" ? (
              <p role="status" className="dp-share-warn">
                剪贴板不可用：请手动选中上面的链接复制。
              </p>
            ) : null}
          </section>
        ) : null}

        <section className="dp-share-list" aria-label="已有分享">
          <div className="dp-share-list-head">
            <span className="dp-label">已有分享</span>
            <span className="dp-tiny-note">{shares.length} 条</span>
          </div>

          {sharesQuery.isPending ? (
            <LoadingState label="正在读取分享列表…" />
          ) : sharesQuery.isError ? (
            <p className="dp-share-warn" role="status">
              分享列表读不到（{errorMessage(sharesQuery.error, "接口不可用")}）。
              已生成的链接仍可用，撤销后页面会刷新。
            </p>
          ) : shares.length === 0 ? (
            <p className="dp-share-empty">还没有分享链接。生成一条后，无痕窗口也能打开看故事板。</p>
          ) : (
            <ul className="dp-share-rows">
              {shares.map((share) => {
                const episode = share.episodeId
                  ? episodes.find((item) => item.id === share.episodeId) ?? null
                  : null;
                const expired = isShareExpired(share.expiresAt);
                const displayToken = maskToken(share.token);
                const rowKey = share.id || share.token;
                const confirming = confirmKey === rowKey;
                const busy = revoking === share.id;
                return (
                  <li
                    key={rowKey}
                    className="dp-share-row"
                    data-expired={expired ? "true" : undefined}
                  >
                    <div className="dp-share-row-main">
                      <code className="dp-share-token" title="掩码 token（完整值只在生成时显示一次）">
                        {displayToken}
                      </code>
                      <span className="dp-share-row-scope">
                        {shareScopeLabel(share.scope, episode)}
                      </span>
                      <span className="dp-share-row-exp">
                        {formatDateTime(share.expiresAt)} · {shareExpiryNote(share.expiresAt)}
                      </span>
                      <span className="dp-share-row-flag">
                        {share.allowDownload ? "允许下载" : "仅浏览"}
                      </span>
                    </div>

                    <div className="dp-share-row-actions">
                      {/* 列表里的 url/token 都是掩码：复制按钮禁用，只有刚创建的那条能复制 */}
                      <button
                        type="button"
                        className="dp-btn sm"
                        disabled
                        title="完整链接只在创建时显示一次"
                        aria-label={`复制分享 ${displayToken}（不可用：完整链接只在创建时显示一次）`}
                      >
                        复制
                      </button>

                      {confirming ? (
                        <div className="dp-share-confirm" role="group" aria-label="确认撤销分享">
                          <span>撤销后立即 404，确认？</span>
                          <button
                            type="button"
                            className="dp-btn sm danger"
                            disabled={busy || !share.id}
                            onClick={() => void handleRevoke(share)}
                          >
                            {busy ? "撤销中…" : "确认撤销"}
                          </button>
                          <button
                            type="button"
                            className="dp-btn sm ghost"
                            onClick={() => setConfirmKey(null)}
                          >
                            取消
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="dp-btn sm danger"
                          aria-label={`撤销分享 ${displayToken}`}
                          disabled={revoking !== null || !share.id}
                          title={share.id ? "撤销后公开页立即 404" : "缺少分享 id，无法撤销"}
                          onClick={() => setConfirmKey(rowKey)}
                        >
                          撤销
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <p className="dp-share-foot">
          完整 token 只在创建时返回一次，服务端只存 sha256；撤销按 id 删除，撤销或过期即 404，
          不暴露项目其它集与账号信息。
        </p>
      </div>
    </div>
  );
}
